'use client';

import clsx from 'clsx';
import { type ForwardedRef, type UIEventHandler, useImperativeHandle, useRef } from 'react';
import { useId } from 'react-aria';
import {
  Button as RACButton,
  Collection as RACCollection,
  ComboBox as RACComboBox,
  Group as RACGroup,
  Header as RACHeader,
  ListBoxItem as RACListBoxItem,
  ListBoxSection as RACListBoxSection,
  Text as RACText,
  type ComboBoxProps as RACComboBoxProps,
  type ListBoxItemProps as RACListBoxItemProps,
  ComboBoxValue as RACComboBoxValue,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconChevronDown } from '../../icons/index.js';
import { isProduction } from '../../utils/is-production.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { CheckboxIndicator } from '../internal/checkbox-indicator/checkbox-indicator.js';
import { CollapsibleList } from '../internal/collapsible-list/collapsible-list.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { TagGroup, type TagGroupProps } from '../tag/tag-group.js';
import { Tag, type TagProps } from '../tag/tag.js';
import type {
  ApiProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  FieldWithPlaceholderProps,
  Key,
  StringLikeChildren,
} from '../types.js';
import { textStyle } from '../typography/utils.js';
import { TagFieldInput } from './tag-field-input.js';
import { TagFieldListBox } from './tag-field-list-box.js';

/** Represents the state of the `TagField` component for validation purposes. */
export interface TagFieldValidationValue {
  /** The selected keys in the TagField. */
  selectedKeys: Set<Key> | null;
  /** The value of the TagField input. */
  inputValue: string;
}

export interface TagFieldProps<T extends CollectionItem = CollectionItem>
  extends
    CommonProps,
    FieldWithPlaceholderProps<TagFieldValidationValue>,
    CollectionProps<T>,
    ApiProps<TagFieldApi>,
    Pick<
      RACComboBoxProps<T>,
      | 'isRequired'
      | 'isDisabled'
      | 'isInvalid'
      | 'isReadOnly'
      | 'inputValue'
      | 'defaultInputValue'
      | 'autoFocus'
      | 'onInputChange'
      | 'onFocus'
      | 'onBlur'
      | 'onOpenChange'
    >,
    Pick<TagGroupProps<T>, 'renderEmptyState'> {
  /** The currently selected keys in the collection (controlled). */
  selectedKeys?: Iterable<Key>;
  /** The initial selected keys in the collection (uncontrolled). */
  defaultSelectedKeys?: Iterable<Key>;
  /** Handler that is called when the selection changes. */
  onSelectionChange?: (keys: Set<Key>) => void;
  /**
   * Whether the list of options should be virtualized. Use this as a performance optimization for large lists.
   * @default false
   */
  isVirtualized?: boolean;
  /** Handler that is called when the list of items is scrolled. */
  onScroll?: UIEventHandler<HTMLDivElement>;
}

/** The imperative API exposed by the `TagField` component. */
export interface TagFieldApi {
  /** Focuses the input field and opens the list of items. */
  focus: () => void;
}

function TagField<T extends CollectionItem>(
  {
    label,
    description,
    error,
    items,
    children,
    placeholder,
    UNSAFE_className,
    UNSAFE_style,
    validate,
    isReadOnly,
    onScroll,
    selectedKeys,
    defaultSelectedKeys,
    onSelectionChange,
    id,
    renderEmptyState,
    isVirtualized = false,
    apiRef,
    ...props
  }: TagFieldProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, isRequired, isDisabled } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('TagField requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`TagField` requires `children` to be a function when using `items` prop');
  }

  const inputId = useId(id);

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  return (
    <RACComboBox
      {...props}
      ref={ref}
      className={clsx('cim-combo-box', 'cim-tag-field', UNSAFE_className)}
      style={UNSAFE_style}
      id={inputId}
      selectionMode="multiple"
      value={selectedKeys ? Array.from(selectedKeys) : undefined}
      defaultValue={defaultSelectedKeys ? Array.from(defaultSelectedKeys) : undefined}
      onChange={(keys) => onSelectionChange?.(new Set(keys))}
      menuTrigger="focus"
      isReadOnly={isReadOnly}
      data-readonly={isReadOnly ? true : undefined}
      validate={
        validate
          ? ({ inputValue, value }) => {
              return validate({ inputValue, selectedKeys: new Set(value as unknown as Key[]) });
            }
          : undefined
      }
    >
      <FormFieldLabel isRequired={isRequired} isDisabled={isDisabled}>
        {label}
      </FormFieldLabel>

      <RACGroup className="cim-combo-box-input-group">
        <TagFieldInput
          inputRef={inputRef}
          aria-describedby={props['aria-describedby']}
          isReadOnly={isReadOnly}
          placeholder={placeholder}
        />
        <RACButton className="cim-combo-box-button">
          <IconChevronDown />
        </RACButton>
      </RACGroup>

      <FormFieldError>{error}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>

      <RACComboBoxValue>
        {({ state }) => (
          <TagGroup
            aria-labelledby={inputId}
            disabledKeys={isDisabled ? state.selectedItems.map((item) => item.key) : undefined}
            renderEmptyState={renderEmptyState}
            onRemove={
              isReadOnly
                ? undefined
                : (keys) => {
                    if (Array.isArray(state.value)) {
                      state.setValue(state.value.filter((k) => !keys.has(k)));
                    }
                  }
            }
          >
            {state.selectedItems.map((item) => (
              <Tag key={item.key} id={item.key} isDisabled={(item.props as TagProps).isDisabled}>
                {item.textValue}
              </Tag>
            ))}
          </TagGroup>
        )}
      </RACComboBoxValue>

      <CollapsibleList
        offset={5} // 1px border + 4px actual offset
        maxHeight={315}
      >
        <TagFieldListBox<T> items={items} onScroll={onScroll} isVirtualized={isVirtualized}>
          {children}
        </TagFieldListBox>
      </CollapsibleList>
    </RACComboBox>
  );
}

/**
 * Allows users to filter a collapsible list and select multiple items from it.
 *
 * See [tag field usage guidelines](https://ui.cimpress.io/components/tag-field).
 */
const _TagField = withStyleProps(forwardRef(TagField), 'TagField');

export { _TagField as TagField };

export interface TagFieldItemProps<T extends CollectionItem> extends Pick<
  RACListBoxItemProps<T>,
  'isDisabled' | 'onHoverStart' | 'onHoverEnd'
> {
  /** The ID of the item. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the label. */
  children: StringLikeChildren;
}

/** Renders a single list item within `TagField`. */
export function TagFieldItem<T extends CollectionItem>({ children, ...props }: TagFieldItemProps<T>) {
  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  return (
    <RACListBoxItem {...props} className="cim-tag-field-item cim-checkbox-indicator-wrapper" textValue={textValue}>
      {({ isDisabled }) => (
        <>
          <CheckboxIndicator />

          <RACText slot="label" className={textStyle({ variant: 'medium', tone: isDisabled ? 'muted' : 'base' })}>
            {children}
          </RACText>
        </>
      )}
    </RACListBoxItem>
  );
}

TagFieldItem.displayName = 'TagFieldItem';

export interface TagFieldSectionProps<T extends CollectionItem> extends CollectionProps<T> {
  /** The ID of the section. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the section title. */
  title: string;
}

/** Groups list items within `TagField` into a section. */
export function TagFieldSection<T extends CollectionItem>({
  title,
  children,
  items,
  ...props
}: TagFieldSectionProps<T>) {
  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`TagFieldSection` requires `children` to be a function when using `items` prop');
  }

  return (
    <RACListBoxSection {...props} className="cim-combo-box-section">
      <RACHeader
        className={clsx('cim-combo-box-section-header', textStyle({ variant: 'small-semibold', tone: 'subtle' }))}
      >
        {title}
      </RACHeader>
      <RACCollection items={items}>{children}</RACCollection>
    </RACListBoxSection>
  );
}

TagFieldSection.displayName = 'TagFieldSection';
